import { type Doc } from "building-types/doc";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { HomeSettingsRW } from "building-types/settings/homeSettings";
import { type SettingsSet } from "building-types/settings/settingsSet";
import type { RWTable, Table } from "common/data/table";
import { optionSetDefaultOptionId } from "doc/options/optionSetBaseUtils";
import { evalFormulas } from "../formula/evalFormulas";
import { getSettingsByParentId, mergeSettingsSet } from "../mergeSettingsSet";
import { entityIsGlobalSettingsSet } from "../settingsTools";

const EMPTY_OPTION_SETS: Doc["optionSets"] = {};

export interface FlattenSettingsSetOptions {
  settings: Doc["settings"];
  settingsSets: Doc["settingsSets"];
  entityDisabledByGroup?: Table<boolean>;
  optionChoices: OptionChoices;
  optionSets?: Doc["optionSets"];
}

/**
 * Returns settings sets with `sortIndex <= maxSortIndex`. Returns the input table
 * by identity when every set already satisfies the cap, so downstream WeakMap caches stay hot.
 */
export function settingsSetsUpToSortIndex(
  settingsSets: Doc["settingsSets"],
  maxSortIndex: number,
): Doc["settingsSets"] {
  for (const id in settingsSets) {
    if (settingsSets[id].sortIndex > maxSortIndex) {
      const filtered: RWTable<SettingsSet> = {};
      for (const settingsSetId in settingsSets) {
        const settingsSet = settingsSets[settingsSetId];
        if (settingsSet.sortIndex <= maxSortIndex) {
          filtered[settingsSetId] = settingsSet;
        }
      }
      return filtered;
    }
  }
  return settingsSets;
}

export function evaluateActiveSettingsSets(props: FlattenSettingsSetOptions): SettingsSet[] {
  const { settingsSets, optionChoices, optionSets = EMPTY_OPTION_SETS, entityDisabledByGroup } = props;

  const evaluatedSettingsSets: Table<SettingsSet> = evalFormulas(
    settingsSets,
    (id) => optionChoices[id] ?? (optionSets[id] ? optionSetDefaultOptionId(optionSets[id], optionSets) : undefined),
    undefined,
    entityDisabledByGroup,
  );

  return Object.values(evaluatedSettingsSets)
    .filter((x) => !x.disabled)
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

export function flattenSettingsSets(mergedSettings: HomeSettingsRW, props: FlattenSettingsSetOptions) {
  const { settings } = props;
  const sortedSets = evaluateActiveSettingsSets(props);

  const settingsByParentId = getSettingsByParentId(settings);
  const activeSettingsSets: RWTable<SettingsSet> = {};
  for (const settingsSet of sortedSets) {
    // Targeted Settings sets will be merged at the entity instance
    if (!settingsSet.type || entityIsGlobalSettingsSet(settingsSet)) {
      mergeSettingsSet(mergedSettings, settingsSet.id, settings, false, settingsByParentId);
    }

    activeSettingsSets[settingsSet.id] = settingsSet;
  }

  return { mergedSettings, activeSettingsSets };
}
